import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, retry } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * A unit of delivery work on a project, grouped into sprints.
 *
 * Distinct from WeekPlanTask and MilestonePlanTask: those answer "what is this week about"
 * and "what has the client been promised". This is the backlog underneath them.
 */
export interface ProjectTask {
  id: string;
  projectId: string;
  /** Free text — whatever the team calls its sprints. */
  sprintNumber?: string | null;
  taskNumber?: string | null;
  name: string;
  description?: string | null;
  startDate?: string | null;
  /** When the work is due. */
  targetDate?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  /** To Do, In Progress, Blocked or Done. */
  progress: string;
  progressPercentage: number;
  completedDate?: string | null;
  displayOrder: number;
  /**
   * Total effort as TRUE decimal hours, summed server-side via TimesheetHours — the same
   * helper the Work Log totals with. Never stored on the task, so logging more adds rather
   * than overwrites.
   */
  totalEffortHours: number;
  /** The same total pre-formatted as "Xh Ymin". Prefer this for display. */
  totalEffortDisplay: string;
  /** The entries behind the total, newest first. */
  effortEntries: ProjectTaskEffort[];
  createdDate?: string;
  updatedDate?: string | null;
}

/** One logged effort entry against a task. */
export interface ProjectTaskEffort {
  id: string;
  projectTaskId: string;
  /**
   * As stored, in hours.minutes: 2.30 means 2h 30m. Never add these with +; use
   * sumHoursMinutes from utils/timesheet-hours.util, as the Work Log does.
   */
  hours: number;
  /** This entry formatted as "Xh Ymin". Prefer this for display. */
  hoursDisplay: string;
  effortDate: string;
  notes?: string | null;
  employeeId?: string | null;
  employeeName?: string | null;
  createdDate?: string;
}

/**
 * One project a developer is allocated to, with its backlog — one expandable group on the
 * Work Log task page.
 */
export interface MyProjectTasks {
  projectId: string;
  projectName: string;
  projectCode?: string | null;
  clientName?: string | null;
  tasks: ProjectTask[];
  taskCount: number;
  doneCount: number;
}

/** Payload for logging effort on its own, outside a task save. */
export interface CreateProjectTaskEffortDto {
  projectTaskId: string;
  /** hours.minutes, as the Work Log stores it: 2.30 means 2h 30m. */
  hours: number;
  /** Server defaults to today when omitted. */
  effortDate?: string | null;
  notes?: string | null;
  employeeId?: string | null;
}

/** Create/update payload. Server-set fields are deliberately absent. */
export interface CreateProjectTaskDto {
  projectId: string;
  sprintNumber?: string | null;
  /** Leave blank on create and the server fills in the next number for the project. */
  taskNumber?: string | null;
  name: string;
  description?: string | null;
  startDate?: string | null;
  /** When the work is due. */
  targetDate?: string | null;
  ownerId?: string | null;
  progress: string;
  progressPercentage: number;
  completedDate?: string | null;
  displayOrder: number;
  /**
   * Hours to log with this save, in hours.minutes (2.30 = 2h 30m) as the Work Log uses.
   * Always ADDS an entry — sending 2.30 twice leaves 5h logged. Null or 0 logs nothing,
   * which is what an ordinary edit sends.
   */
  effortHours?: number | null;
  effortNotes?: string | null;
}

/** Workflow states, matching the values the API stores. */
export const PROJECT_TASK_PROGRESS_OPTIONS: { label: string; value: string }[] = [
  { label: 'To Do', value: 'To Do' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Blocked', value: 'Blocked' },
  { label: 'Done', value: 'Done' },
];

/**
 * Quarter steps, rather than a 0–100 spinner. Nobody meaningfully distinguishes 63% from
 * 65%, and stepping a spinner one percent at a time is slow.
 *
 * 100% is deliberately absent: the server forces a Done task to 100 and holds anything
 * else below it, so offering 100 on a To Do task would silently save 99. The form adds it
 * back only when the state is Done.
 */
export const PROJECT_TASK_PERCENT_OPTIONS: { label: string; value: number }[] = [
  { label: '0%', value: 0 },
  { label: '25%', value: 25 },
  { label: '50%', value: 50 },
  { label: '75%', value: 75 },
];

@Injectable({ providedIn: 'root' })
export class ProjectTaskService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/ProjectTask`;

  getByProjectId(projectId: string): Observable<ProjectTask[]> {
    return this.http.get<ProjectTask[]>(`${this.apiUrl}/project/${projectId}`).pipe(retry(2));
  }

  getById(id: string): Observable<ProjectTask> {
    return this.http.get<ProjectTask>(`${this.apiUrl}/${id}`).pipe(retry(2));
  }

  add(dto: CreateProjectTaskDto): Observable<ProjectTask> {
    return this.http.post<ProjectTask>(this.apiUrl, dto);
  }

  update(id: string, dto: CreateProjectTaskDto): Observable<ProjectTask> {
    return this.http.put<ProjectTask>(`${this.apiUrl}/${id}`, dto);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * The signed-in user's own backlog, grouped by the projects they are allocated to.
   *
   * Sends no employee id: the server derives it from the token, so this cannot be pointed
   * at someone else. Year and month default to the current month server-side.
   */
  getMyProjectTasks(year?: number, month?: number): Observable<MyProjectTasks[]> {
    const params: string[] = [];
    if (year) params.push(`year=${year}`);
    if (month) params.push(`month=${month}`);
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<MyProjectTasks[]>(`${this.apiUrl}/my-tasks${query}`).pipe(retry(2));
  }

  /** Logs effort without touching the rest of the task. Returns the task with its new total. */
  addEffort(dto: CreateProjectTaskEffortDto): Observable<ProjectTask> {
    return this.http.post<ProjectTask>(`${this.apiUrl}/effort`, dto);
  }

  /** Removes one entry — how a mistyped figure is corrected. Returns the updated task. */
  deleteEffort(effortId: string): Observable<ProjectTask> {
    return this.http.delete<ProjectTask>(`${this.apiUrl}/effort/${effortId}`);
  }
}
